import * as Popover from '@radix-ui/react-popover'
import { RotateCw, Settings } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useSessionOptional } from '../session-context'
import { useEditorSettings } from '../settings-modal/editor-settings-modal-context'
import { useTimer } from './timer-context'
import { TimerDisplay } from './timer-display'

function formatTimeLarge(ms: number, roundUp = false): string {
  const abs = Math.abs(ms)
  const totalSeconds = roundUp ? Math.ceil(abs / 1000) : Math.floor(abs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [
    String(hours).padStart(2, '0'),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0'),
  ].join(':')
}

export function TimerPopover() {
  const ctx = useTimer()
  const sessionCtx = useSessionOptional()
  const [open, setOpen] = useState(false)
  const editorSettings = useEditorSettings()
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Derive the active tab from context rather than mirroring it in local
  // state, so external changes (toolbar chevron toggle, timer completion,
  // problem-slug reset) stay in sync with what the popover shows.
  const popoverTab = ctx.activeView

  // Sync popover tab selection to the toolbar display
  const handleTabChange = (tab: 'stopwatch' | 'timer') => {
    ctx.setActiveView(tab)
  }
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)

  // Track last non-zero durationMs so programming view can show last programmed value
  const lastDurationRef = useRef(ctx.timer.durationMs)
  if (ctx.timer.durationMs > 0) {
    lastDurationRef.current = ctx.timer.durationMs
  }

  // When endTimer clears durationMs to 0, populate inputs from last known duration
  const prevDurationRef = useRef(ctx.timer.durationMs)
  useEffect(() => {
    if (
      prevDurationRef.current > 0 &&
      ctx.timer.durationMs === 0 &&
      lastDurationRef.current > 0
    ) {
      const totalSeconds = Math.floor(lastDurationRef.current / 1000)
      setHours(Math.floor(totalSeconds / 3600))
      setMinutes(Math.floor((totalSeconds % 3600) / 60))
    }
    prevDurationRef.current = ctx.timer.durationMs
  }, [ctx.timer.durationMs])

  // Ensure a session exists before starting any instrument —
  // starting the clock is an explicit user action that should persist
  const ensureSession = () => {
    if (!sessionCtx?.session) {
      sessionCtx?.createSession()
    }
  }

  const handleStartTimer = () => {
    const durationMs = (hours * 3600 + minutes * 60) * 1000
    if (durationMs <= 0) return
    ensureSession()
    ctx.startTimer(durationMs)
    ctx.setActiveView('timer')
  }

  const stopwatchIsRunning = ctx.stopwatch.status === 'running'
  const timerIsActive =
    ctx.timer.status === 'running' ||
    ctx.timer.status === 'paused' ||
    ctx.timer.status === 'overage'

  return (
    <div className="flex items-center gap-1">
      <TimerDisplay />
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="Timer options"
            className="text-muted-foreground hover:text-foreground p-0.5"
          >
            <RotateCw className="h-3.5 w-3.5" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="center"
            sideOffset={8}
            className="border-border bg-background z-50 w-64 rounded-lg border p-4 shadow-lg"
          >
            {/* Tabs */}
            <div className="mb-4 flex gap-2">
              <button
                type="button"
                onClick={() => handleTabChange('stopwatch')}
                className={`flex-1 rounded-lg border-2 p-3 text-center text-xs font-semibold transition-colors ${
                  popoverTab === 'stopwatch'
                    ? 'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-950/30'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                Stopwatch
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('timer')}
                className={`flex-1 rounded-lg border-2 p-3 text-center text-xs font-semibold transition-colors ${
                  popoverTab === 'timer'
                    ? 'border-orange-500 bg-orange-50 text-orange-600 dark:bg-orange-950/30'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                Timer
              </button>
            </div>

            {/* Stopwatch tab content */}
            {popoverTab === 'stopwatch' && (
              <div className="space-y-3">
                <div className="text-center font-mono text-2xl font-bold text-blue-500">
                  {formatTimeLarge(ctx.stopwatch.elapsedMs)}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      if (stopwatchIsRunning) {
                        ctx.pauseStopwatch()
                      } else {
                        ensureSession()
                        ctx.startStopwatch()
                      }
                    }}
                  >
                    {stopwatchIsRunning ? 'Pause' : 'Start'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={ctx.resetStopwatch}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            )}

            {/* Timer tab content */}
            {popoverTab === 'timer' && (
              <div className="space-y-3">
                {!timerIsActive ? (
                  <>
                    {/* Show leftover time with reset if timer has residual time */}
                    {ctx.timer.durationMs > 0 && (
                      <div className="space-y-2">
                        <div className="text-center font-mono text-2xl font-bold text-orange-500">
                          {formatTimeLarge(ctx.timer.remainingMs, true)}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              ensureSession()
                              ctx.startTimer(ctx.timer.durationMs)
                            }}
                          >
                            Resume
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="flex-1"
                            onClick={ctx.resetTimer}
                          >
                            Reset
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full"
                          onClick={ctx.endTimer}
                        >
                          End Timer
                        </Button>
                      </div>
                    )}

                    {/* Hour/minute inputs for setting a new timer */}
                    {ctx.timer.durationMs === 0 && (
                      <>
                        <div className="flex items-center justify-center gap-2">
                          <div className="text-center">
                            <input
                              type="number"
                              min={0}
                              max={23}
                              value={hours}
                              aria-label="Hours"
                              onChange={(e) =>
                                setHours(
                                  Math.max(
                                    0,
                                    Math.min(23, Number(e.target.value)),
                                  ),
                                )
                              }
                              className="border-border bg-muted/50 h-12 w-14 rounded-lg border text-center font-mono text-2xl font-bold"
                            />
                            <div className="text-muted-foreground mt-1 text-xs">
                              hr
                            </div>
                          </div>
                          <div className="text-center">
                            <input
                              type="number"
                              min={0}
                              max={59}
                              value={minutes}
                              aria-label="Minutes"
                              onChange={(e) =>
                                setMinutes(
                                  Math.max(
                                    0,
                                    Math.min(59, Number(e.target.value)),
                                  ),
                                )
                              }
                              className="border-border bg-muted/50 h-12 w-14 rounded-lg border text-center font-mono text-2xl font-bold"
                            />
                            <div className="text-muted-foreground mt-1 text-xs">
                              min
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={handleStartTimer}
                          disabled={hours === 0 && minutes === 0}
                        >
                          Start Timer
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Timer running/paused display */}
                    <div
                      className={`text-center font-mono text-2xl font-bold ${
                        ctx.timer.status === 'overage'
                          ? 'text-red-500'
                          : 'text-orange-500'
                      }`}
                    >
                      {ctx.timer.remainingMs < 0 ? '-' : ''}
                      {formatTimeLarge(ctx.timer.remainingMs, true)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          if (
                            ctx.timer.status === 'running' ||
                            ctx.timer.status === 'overage'
                          ) {
                            ctx.pauseTimer()
                          } else {
                            ensureSession()
                            ctx.startTimer(ctx.timer.durationMs)
                          }
                        }}
                      >
                        {ctx.timer.status === 'running' ||
                        ctx.timer.status === 'overage'
                          ? 'Pause'
                          : 'Resume'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="flex-1"
                        onClick={ctx.resetTimer}
                      >
                        Reset
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="w-full"
                      onClick={ctx.endTimer}
                    >
                      End Timer
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Settings link */}
            <div className="border-border mt-3 border-t pt-3 text-center">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  // This button unmounts with the popover; return focus to the
                  // popover's trigger when the settings modal closes.
                  editorSettings.open('timer', {
                    returnFocusTo: triggerRef.current,
                  })
                }}
                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs"
              >
                <Settings className="h-3.5 w-3.5" />
                Settings
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}
